/**
 * ErrorFallback — fallback UI rendered by react-error-boundary when a render
 * error escapes the wrapped component subtree.
 *
 * Spec: memory-bank/creative/TASK-079-phase2-error-fallback.md
 *
 * Two visual variants:
 *   - 'route'  : per-route fallback rendered inside the AppLayout's app-main slot.
 *                Reload calls resetErrorBoundary() (soft reset). Go home is a
 *                react-router <Link to="/">.
 *   - 'root'   : root-level fallback rendered without AppLayout chrome.
 *                Reload calls window.location.reload() (hard). Go home is a hard
 *                window.location.href = '/'.
 *
 * The Report link only renders when userId is truthy. Clicking it calls
 * Sentry.captureException with the user id and the route tag, then swaps the
 * link text to "Reported — thank you" and sets aria-disabled.
 *
 * The support reference is derived once on mount: prefer Sentry.lastEventId(),
 * fall back to crypto.randomUUID(). Only the first 8 chars are displayed; the
 * Copy button writes the full reference to the clipboard.
 */
import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './error-fallback.css';

const COPY_FEEDBACK_MS = 2000;

const COPY = {
  route: {
    body:
      "We hit an unexpected error on this page. This doesn't affect the rest of the portal — you can navigate using the menu above.",
  },
  root: {
    body:
      "We ran into an unexpected error and couldn't load the page. Please try reloading, or return to the home page.",
  },
};

function WarningIcon() {
  // Inline SVG — no external asset, kept under the 200-byte budget per spec.
  return (
    <svg
      className="error-fallback__icon"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 2 1 22h22L12 2zm0 6v6m0 4v.01"
        stroke="#f59e0b"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M5 12l5 5 9-11"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="8"
        y="8"
        width="12"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function ErrorFallback({
  error,
  resetErrorBoundary,
  routeName,
  userId,
  variant,
}) {
  const isRoot = variant === 'root';

  // Derive a stable support reference once, on mount.
  const [supportRef] = useState(() => {
    let id;
    try {
      id = Sentry.lastEventId && Sentry.lastEventId();
    } catch {
      id = undefined;
    }
    if (!id) {
      id =
        (globalThis.crypto && globalThis.crypto.randomUUID && globalThis.crypto.randomUUID()) ||
        // Last-resort fallback (jsdom without crypto.randomUUID)
        `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
    }
    return id;
  });

  const [reported, setReported] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleReload = useCallback(() => {
    if (isRoot) {
      window.location.reload();
    } else {
      resetErrorBoundary();
    }
  }, [isRoot, resetErrorBoundary]);

  const handleHomeClick = useCallback((event) => {
    if (isRoot) {
      event.preventDefault();
      window.location.href = '/';
    }
  }, [isRoot]);

  const handleReport = useCallback(() => {
    if (reported) return;
    try {
      Sentry.captureException(error, {
        user: { id: userId },
        tags: {
          route: routeName ?? window.location.pathname,
        },
      });
    } catch {
      // Sentry not initialized in dev/test — captureException is a no-op so
      // this is mostly defensive. Don't surface secondary failure to the user.
    }
    setReported(true);
  }, [error, userId, routeName, reported]);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(supportRef);
      }
    } catch {
      // Best-effort copy. Older browsers / restricted iframes may reject.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  }, [supportRef]);

  const containerClassName = isRoot
    ? 'error-fallback error-fallback--root'
    : 'error-fallback';

  const refDisplay = supportRef.slice(0, 8);

  return (
    <div
      className={containerClassName}
      role="alert"
      data-testid="error-fallback"
      data-variant={variant}
    >
      <div className="error-fallback__card">
        <div className="error-fallback__icon-row">
          <WarningIcon />
          <h2 className="error-fallback__heading">Something went wrong</h2>
        </div>

        <p
          className="error-fallback__body"
          data-testid="error-fallback-body"
        >
          {isRoot ? COPY.root.body : COPY.route.body}
        </p>

        <div className="error-fallback__actions">
          <button
            type="button"
            className="btn-primary error-fallback__reload"
            data-testid="error-fallback-reload"
            onClick={handleReload}
            autoFocus
          >
            Reload page
          </button>
          {isRoot ? (
            <a
              className="btn-secondary error-fallback__home"
              data-testid="error-fallback-home"
              href="/"
              onClick={handleHomeClick}
            >
              Go home
            </a>
          ) : (
            <Link
              className="btn-secondary error-fallback__home"
              data-testid="error-fallback-home"
              to="/"
            >
              Go home
            </Link>
          )}
        </div>

        {userId ? (
          <button
            type="button"
            className="error-fallback__report"
            data-testid="error-fallback-report"
            onClick={handleReport}
            aria-disabled={reported ? 'true' : undefined}
          >
            {reported ? 'Reported — thank you' : 'Report this issue'}
          </button>
        ) : null}

        <div className="error-fallback__ref">
          <span
            className="error-fallback__ref-label"
            data-testid="error-fallback-ref"
          >
            Support ref: <code>{refDisplay}</code>
          </span>
          <button
            type="button"
            className="error-fallback__ref-copy"
            data-testid="error-fallback-ref-copy"
            onClick={handleCopy}
            aria-label="Copy support reference to clipboard"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            <span className="error-fallback__ref-copy-text">
              {copied ? 'Copied' : 'Copy'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
